import { usePage } from "@inertiajs/react"

import type { SharedProps } from "@/types"

export type WeightUnit = "lbs" | "tons" | "tonnes"

const LABELS: Record<WeightUnit, string> = {
  lbs: "lbs",
  tons: "tons",
  tonnes: "tonnes",
}

/** Mirrors WeightConversion::LBS_PER_SHORT_TON / LBS_PER_METRIC_TONNE. */
const DIVISOR: Record<WeightUnit, number> = {
  lbs: 1,
  tons: 2000,
  tonnes: 2204.62,
}

/**
 * Canonical pounds -> a display string in `unit`, with no unit suffix.
 *
 * THE one client-side weight precision rule, shared by `formatWeight` below and
 * by the reports table. The reports table cannot use the hook: a report column
 * carries its OWN unit (the opt-in "(lbs)" companion stays in pounds even on a
 * tons org), so reading the org unit ambiently would render that column in the
 * wrong denomination. Passing the unit in keeps one rule for both callers.
 *
 * 2dp on tons/tonnes, not 1: a ton is 2,000 lbs, so one decimal quantizes to
 * 200-lb steps and a real correction disappears from the page while the ledger
 * records it. Matches Reports::WeightUnits.decimals on the server.
 */
export function formatWeightValue(lbs: number, unit: WeightUnit): string {
  const digits = weightDecimals(unit)
  return (lbs / DIVISOR[unit]).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

/** Decimal places `unit` is shown to. Mirrors Reports::WeightUnits.decimals. */
export function weightDecimals(unit: WeightUnit): number {
  return unit === "lbs" ? 0 : 2
}

/** Canonical pounds -> the numeric value `unit` displays, unrounded. */
export function toDisplayUnits(lbs: number, unit: WeightUnit): number {
  return lbs / DIVISOR[unit]
}

/**
 * Renders an example weight for a placeholder in the org's unit.
 *
 * Callers pass the example in canonical lbs — the same contract every other
 * value crossing this hook follows — so there is one number per field to
 * maintain instead of a per-unit string table that drifts. Placeholders used
 * to be hardcoded at pound scale, which put "e.g. 180,000" under a label
 * reading "(tons)": an example 2000x the value the field wanted.
 */
export function weightExample(lbs: number, unit: WeightUnit): string {
  const value = lbs / DIVISOR[unit]
  // Examples are illustrative, so keep them clean: whole numbers once we're
  // past 10, one decimal below (5,000 lbs => "2.5 tons", not "3").
  const rounded =
    unit === "lbs" || value >= 10
      ? Math.round(value)
      : Math.round(value * 10) / 10
  // Pinned to en-US, unlike formatWeight's deliberate system locale: this
  // string is an example of what to TYPE, and the input mask it sits under
  // (formatNumberWithCommas) hardcodes "," for thousands and "." for the
  // decimal. A locale-formatted "180.000" would demonstrate a value the
  // field itself would never render back.
  return `e.g. ${rounded.toLocaleString("en-US")}`
}

export function useWeightUnit() {
  const { organization } = usePage<SharedProps>().props
  const unit: WeightUnit = organization?.weight_unit || "lbs"

  const unitLabel = LABELS[unit]
  const divisor = DIVISOR[unit]

  function toDisplay(lbs: number): number {
    return toDisplayUnits(lbs, unit)
  }

  function toLbs(displayValue: number): number {
    return displayValue * divisor
  }

  function formatWeight(lbs: number | null | undefined): string {
    if (lbs == null) return "--"
    return `${formatWeightValue(lbs, unit)} ${unitLabel}`
  }

  function example(lbs: number): string {
    return weightExample(lbs, unit)
  }

  return { unit, unitLabel, formatWeight, toDisplay, toLbs, example }
}
